import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Modal, Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import { 
  fetchIllnessReports, 
  submitParentSignature,
  selectReports, 
  selectMeta, 
  selectLoading, 
  selectError,
  selectSubmitLoading 
} from '../../../store/illnessReport/illnessReportSlice';
import { handleImageUpload } from '../../../api/uploads';

const IllnessReports = () => {
  const dispatch = useDispatch();
  const reports = useSelector(selectReports);
  const meta = useSelector(selectMeta);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const submitLoading = useSelector(selectSubmitLoading);

  const [currentPage, setCurrentPage] = useState(1);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [signatureDate, setSignatureDate] = useState('');
  const signaturePadRef = useRef(null);

  useEffect(() => {
    dispatch(fetchIllnessReports({ page: currentPage, limit: 10 }));
  }, [dispatch, currentPage]);

  const handleSignReport = (report) => {
    setSelectedReport(report);
    setSignatureDate('');
    setShowSignatureModal(true);
  };

  const handleCloseModal = () => {
    setShowSignatureModal(false);
    setSelectedReport(null);
    setSignatureDate('');
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleSubmitSignature = async () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      Swal.fire({
        icon: 'warning',
        title: 'Signature Required',
        text: 'Please provide your signature before submitting.'
      });
      return;
    }

    if (!signatureDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Date Required',
        text: 'Please select a signature date.'
      });
      return;
    }

    try {
      // Get signature as data URL
      const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
      
      // Convert data URL to Blob
      const response = await fetch(signatureDataURL);
      const blob = await response.blob();
      
      // Create a file from blob
      const timestamp = new Date().getTime();
      const fileName = `signature_${timestamp}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      
      // Upload to AWS
      const signatureUrl = await handleImageUpload(file, 'signatures/', 'image/png');

      // Submit signature
      await dispatch(submitParentSignature({
        reportId: selectedReport.id,
        parent_signature: signatureUrl,
        parent_signature_date: signatureDate
      })).unwrap();

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Signature submitted successfully!'
      });

      handleCloseModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error || 'Failed to submit signature'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Container>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="header-title">
                <h4 className="card-title">Illness Reports</h4>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center p-5">
                  <p className="text-muted">No illness reports available</p>
                </div>
              ) : (
                <>
                  <Row>
                    {reports.map((report) => (
                      <Col lg="12" key={report.id} className="mb-3">
                        <Card className="shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h5 className="mb-2">
                                  Report Date: {formatDate(report.created_at)}
                                </h5>
                                <Badge bg={report.parent_signature ? 'success' : 'warning'}>
                                  {report.parent_signature ? 'Signed' : 'Pending Signature'}
                                </Badge>
                              </div>
                              {!report.parent_signature && (
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  onClick={() => handleSignReport(report)}
                                >
                                  <i className="icon material-symbols-outlined me-1">edit</i>
                                  Sign Report
                                </Button>
                              )}
                            </div>

                            <Row>
                              <Col md="6">
                                <p className="mb-2">
                                  <strong>Symptoms:</strong>
                                  <br />
                                  {report.symptoms}
                                </p>
                                <p className="mb-2">
                                  <strong>Requested Action from Parent:</strong>
                                  <br />
                                  {report.requested_action_parent}
                                </p>
                                <p className="mb-2">
                                  <strong>Expected Return Date:</strong> {formatDate(report.return_date)}
                                </p>
                              </Col>
                              <Col md="6">
                                {report.teacher_signature && (
                                  <div className="mb-3">
                                    <p className="mb-2">
                                      <strong>Teacher Signature:</strong>
                                      <br />
                                      <img 
                                        src={report.teacher_signature} 
                                        alt="Teacher Signature" 
                                        style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #ddd', padding: '5px' }}
                                      />
                                    </p>
                                    <p className="mb-2">
                                      <strong>Teacher Signature Date:</strong> {formatDate(report.teacher_signature_date)}
                                    </p>
                                  </div>
                                )}
                                {report.parent_signature && (
                                  <div>
                                    <p className="mb-2">
                                      <strong>Parent Signature:</strong>
                                      <br />
                                      <img 
                                        src={report.parent_signature} 
                                        alt="Parent Signature" 
                                        style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #ddd', padding: '5px' }}
                                      />
                                    </p>
                                    <p className="mb-2">
                                      <strong>Parent Signature Date:</strong> {formatDate(report.parent_signature_date)}
                                    </p>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  {/* Pagination */}
                  {meta && meta.last_page > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav>
                        <ul className="pagination">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(meta.last_page)].map((_, index) => (
                            <li 
                              key={index + 1} 
                              className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                            >
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(index + 1)}
                              >
                                {index + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === meta.last_page ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === meta.last_page}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Signature Modal */}
      <Modal 
        show={showSignatureModal} 
        onHide={handleCloseModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sign Illness Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <div className="mb-3">
              <p><strong>Symptoms:</strong> {selectedReport.symptoms}</p>
              <p><strong>Expected Return Date:</strong> {formatDate(selectedReport.return_date)}</p>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Parent Signature *</Form.Label>
            <div className="border rounded" style={{ 
              border: '2px solid #ddd', 
              backgroundColor: '#fff' 
            }}>
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
                backgroundColor="rgb(255, 255, 255)"
              />
            </div>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="mt-2"
              onClick={handleClearSignature}
            >
              Clear Signature
            </Button>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Signature Date *</Form.Label>
            <Form.Control
              type="date"
              value={signatureDate}
              onChange={(e) => setSignatureDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitSignature}
            disabled={submitLoading}
          >
            {submitLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              'Submit Signature'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default IllnessReports;
